// BioForge 2.2 — Command Center bento + lightweight workspace pages.
import { useMemo, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Rocket, ArrowRight, TrendingUp, Activity, BookOpen, FolderKanban,
  Stethoscope, Microscope, Heart, Shield, Pill, Wind, Network, Lightbulb,
  ChevronRight, Loader2, Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  AGENTS, DEMO_SCENARIOS, PROBLEM_LIBRARY, apiGet, apiPost,
  useRunsList, formatUSD, relTime,
} from "@/lib";
import { TechTrendsBar } from "@/viz";
import { useInsights } from "@/shell";
import { SCENARIO_IMAGES } from "@/scenario_images";
import { usePersona, PriorityChips, VentureProgressionCard } from "@/persona";
import { PlatformHookStrip } from "@/platform";
import { formatINR } from "@/currency";

const FEATURED = "fc526bce-d199-433b-8475-8ba28829527f";

// ===========================================================================
// Command Center — 6-widget bento, single viewport
// ===========================================================================
export function CommandCenter() {
  const navigate = useNavigate();
  const persona = usePersona();
  const personaKey = persona.persona;
  const { runs, loading } = useRunsList();
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    apiGet(`/runs/${FEATURED}`, { useCache: false }).then(setFeatured).catch(() => {});
  }, []);

  const completed = runs.filter((r) => r.status === "complete");
  const avgInnovation = completed.length
    ? Math.round(completed.reduce((s, r) => s + (r.scores?.innovation_score?.score || 0), 0) / completed.length)
    : null;
  const avgTtm = completed.length
    ? Math.round(completed.reduce((s, r) => s + (r.scores?.time_to_market_score?.score || 0), 0) / completed.length)
    : null;
  const successRate = completed.length
    ? Math.round(100 * completed.filter((r) => /proceed/i.test(r.founder_review?.overall_recommendation || "")).length / completed.length)
    : null;

  const topTechs = useMemo(() => {
    const counts = {};
    for (const r of completed) {
      for (const t of (r.final_report?.technology_stack || [])) {
        counts[t] = (counts[t] || 0) + 1;
      }
    }
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([tech, count]) => ({ tech, count }));
  }, [completed]);

  const categories = useMemo(() => {
    const defs = [
      { key: "Infectious", icon: Microscope, kw: ["sepsis", "infect", "tuberc", "dengue", "amr", "antibiot", "antimicrob"] },
      { key: "Oncology", icon: Pill, kw: ["cancer", "tumor", "oncolog"] },
      { key: "Maternal", icon: Heart, kw: ["matern", "pregn", "preeclamps", "postpartum"] },
      { key: "Public Health", icon: Shield, kw: ["wastewat", "air qual", "surveill", "screen"] },
      { key: "Diagnostics", icon: Stethoscope, kw: ["diagnos", "screen", "point-of-care", "biomark"] },
      { key: "Chronic", icon: Wind, kw: ["diabet", "kidney", "ckd", "hypert", "heart fail", "chronic"] },
    ];
    return defs.map((d) => ({
      ...d,
      count: runs.filter((r) => d.kw.some((k) => (r.problem || "").toLowerCase().includes(k))).length,
    }));
  }, [runs]);

  const startDemo = async (s) => {
    try {
      const { run_id } = await apiPost("/innovate", s.inputs);
      toast.success(`Generating: ${s.title}`);
      navigate(`/projects/${run_id}/overview`);
    } catch (e) { toast.error(e.message); }
  };

  // Persona-aware widget priority — featured is always anchor at slot A.
  const order = (persona.config.priorityWidgets || []).filter((k) => k !== "featured");
  const [slotB, slotC, slotD, slotE, slotF] = [
    order[0] || "metrics",
    order[1] || "recent",
    order[2] || "categories",
    order[3] || "trends",
    order[4] || "demo",
  ];
  const widgetProps = {
    runs, loading, completed, avgInnovation, avgTtm, successRate,
    topTechs, categories, navigate, startDemo,
  };

  return (
    <div className="px-6 py-5 max-w-[1400px] mx-auto" data-testid="command-center">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-teal-300/80">Command Center · {persona.config.name} Lens</div>
          <h1 className="text-xl font-semibold tracking-tight">{persona.config.cmdCenterHero}</h1>
          <div className="text-xs text-white/55 mt-0.5">{persona.config.cmdCenterSub}</div>
        </div>
        <div className="hidden md:flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate("/projects/new")} data-testid="cmd-new">
            <Rocket className="w-3.5 h-3.5 mr-1.5" />Generate Venture
          </Button>
          <Button size="sm" onClick={() => navigate(`/projects/${FEATURED}/overview`)}
            data-testid="cmd-featured" className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold">
            Open Featured <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
          </Button>
        </div>
      </div>

      <PriorityChips />

      <PlatformHookStrip />

      <div className="grid grid-cols-12 gap-3 auto-rows-min lg:auto-rows-fr"
        style={{ gridAutoRows: "minmax(min-content, 1fr)" }}>
        {/* Slot A — Featured Venture (always anchor) */}
        <Bento className="col-span-12 lg:col-span-7 lg:row-span-2" header="Featured Venture" testId="bento-featured">
          <FeaturedCard doc={featured} onOpen={() => navigate(`/projects/${FEATURED}/overview`)} />
        </Bento>

        {/* Slot B — top right (5 cols row 1) */}
        <div className="col-span-12 lg:col-span-5 min-h-0">
          {renderWidget(slotB, widgetProps)}
        </div>

        {/* Slot C — mid right (5 cols row 2) — either widget or founder roadmap */}
        <div className="col-span-12 lg:col-span-5 min-h-0">
          {personaKey === "founder"
            ? <VentureProgressionCard />
            : renderWidget(slotC, widgetProps)}
        </div>

        {/* Row 3 — 3 widgets across 4-cols each */}
        <div className="col-span-12 lg:col-span-4 min-h-0">{renderWidget(slotD, widgetProps)}</div>
        <div className="col-span-12 lg:col-span-4 min-h-0">{renderWidget(slotE, widgetProps)}</div>
        <div className="col-span-12 lg:col-span-4 min-h-0">{renderWidget(slotF, widgetProps)}</div>
      </div>
    </div>
  );
}

// Widget renderer — keeps slot layout stable, swaps content by persona priority.
function renderWidget(key, p) {
  switch (key) {
    case "metrics":
      return (
        <Bento className="h-full" header="Innovation Metrics" testId="bento-metrics">
          <div className="grid grid-cols-2 gap-2 p-3 h-full">
            <Kpi label="Total ventures" value={p.loading ? "—" : p.runs.length} sub="all statuses" />
            <Kpi label="Avg innovation" value={p.avgInnovation ?? "—"} sub={`across ${p.completed.length} done`} />
            <Kpi label="Avg time-to-market" value={p.avgTtm ?? "—"} sub="higher = faster" />
            <Kpi label="Proceed rate" value={p.successRate != null ? `${p.successRate}%` : "—"} sub="founder verdict" />
          </div>
        </Bento>
      );
    case "recent":
      return (
        <Bento className="h-full" header="Recent Ventures"
          action={<Link to="/projects" className="text-[10px] text-teal-300 hover:text-teal-200">All →</Link>}
          testId="bento-recent">
          <div className="p-2 space-y-1 overflow-y-auto h-full">
            {p.loading && [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
            {!p.loading && p.runs.length === 0 && <div className="text-xs text-white/40 italic p-2">No ventures yet.</div>}
            {p.runs.slice(0, 4).map((r) => (
              <button key={r.run_id} data-testid={`recent-${r.run_id}`}
                onClick={() => p.navigate(`/projects/${r.run_id}/overview`)}
                className="w-full flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-white/[0.03] text-left transition">
                <span className={`w-1.5 h-1.5 rounded-full ${r.status === "complete" ? "bg-emerald-400" : r.status === "running" ? "bg-teal-300 pulse-dot" : "bg-white/30"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium truncate">{r.company_name || r.problem}</div>
                  <div className="text-[10px] text-white/40 truncate">{r.problem}</div>
                </div>
                <span className="text-[10px] mono text-teal-300/80 shrink-0">{r.scores?.innovation_score?.score ?? "—"}</span>
                <span className="text-[10px] text-white/30 shrink-0">{relTime(r.created_at)}</span>
              </button>
            ))}
          </div>
        </Bento>
      );
    case "categories":
      return (
        <Bento className="h-full" header="Problem Categories" testId="bento-categories">
          <div className="grid grid-cols-2 gap-1.5 p-2.5 h-full content-start">
            {p.categories.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.key} data-testid={`cat-${c.key.toLowerCase()}`}
                  className="rounded-md border border-white/[0.06] bg-white/[0.015] p-2 flex items-center gap-1.5">
                  <Icon className="w-3 h-3 text-teal-300/80 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] text-white/80 truncate">{c.key}</div>
                  </div>
                  <span className="text-[11px] font-semibold mono text-teal-200">{c.count}</span>
                </div>
              );
            })}
          </div>
        </Bento>
      );
    case "trends":
      return (
        <Bento className="h-full" header="Technology Trends" testId="bento-trends">
          <div className="p-2.5 h-full">
            {p.topTechs.length === 0
              ? <div className="text-xs text-white/40 italic p-2">Run more ventures to see trends.</div>
              : <TechTrendsBar data={p.topTechs} />}
          </div>
        </Bento>
      );
    case "demo":
      return (
        <Bento className="h-full" header="Demo Launch"
          action={<Link to="/demo" className="text-[10px] text-teal-300 hover:text-teal-200">All →</Link>}
          testId="bento-demo">
          <div className="grid grid-cols-1 gap-2 p-2 h-full overflow-y-auto">
            {DEMO_SCENARIOS.slice(0, 5).map((s) => {
              const img = SCENARIO_IMAGES[s.id];
              const isSepsis = s.id === "sepsis";
              return (
                <button key={s.id} data-testid={`demo-tile-${s.id}`}
                  onClick={() => isSepsis ? p.navigate(`/projects/${FEATURED}/overview`) : p.startDemo(s)}
                  className="relative overflow-hidden rounded-md border border-white/[0.06] hover:border-teal-400/35 aspect-[3/1] group transition">
                  {img && (
                    <img src={img} alt="" loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03] group-hover:brightness-110" />
                  )}
                  <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,11,14,0.92) 0%, rgba(10,11,14,0.65) 55%, rgba(10,11,14,0.20) 100%)" }} />
                  <div className="absolute inset-0 px-3 py-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-white truncate group-hover:text-teal-200">{s.title}</div>
                    </div>
                    {isSepsis && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-400/15 text-emerald-200 border border-emerald-400/40">Pre-run</span>}
                    <ArrowRight className="w-3 h-3 text-white/55 group-hover:text-teal-200 transition" />
                  </div>
                </button>
              );
            })}
          </div>
        </Bento>
      );
    case "featured":
    default:
      return null;
  }
}

function Bento({ className = "", header, action, children, testId }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}
      data-testid={testId}
      className={`bento glass overflow-hidden flex flex-col ${className}`}>
      <div className="bento-header">
        <span>{header}</span>
        {action}
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </motion.section>
  );
}
function Kpi({ label, value, sub }) {
  return (
    <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-2.5">
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">{label}</div>
      <div className="text-2xl font-semibold mt-1 mono">{value}</div>
      {sub && <div className="text-[10px] text-white/30 mt-0.5">{sub}</div>}
    </div>
  );
}
function FeaturedCard({ doc, onOpen }) {
  if (!doc) return <div className="p-6 flex items-center gap-3 text-white/40 text-sm"><Loader2 className="w-4 h-4 animate-spin" />Loading featured venture…</div>;
  const fin = doc.final_report || {};
  const fr = doc.founder_review || {};
  const sc = doc.scores || {};
  const com = doc.outputs?.commercialization_agent || {};
  return (
    <div className="relative p-5 flex flex-col h-full overflow-hidden rounded-[inherit]">
      <img
        src="/scenarios/firstmile-sepsis-hero.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-50"
        aria-hidden="true"
      />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,11,14,0.55) 0%, rgba(10,11,14,0.78) 55%, rgba(10,11,14,0.95) 100%)" }} />
      <div className="relative flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/90 mb-1">Generated venture</div>
          <div className="text-2xl font-semibold tracking-tight truncate text-white">{fin.company_name || "FirstMile Sepsis"}</div>
          <div className="text-sm text-white/70 mt-1 truncate">{doc.inputs?.problem || doc.problem}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/50">Innovation</div>
          <div className="text-3xl font-semibold mono text-teal-200">{sc.innovation_score?.score ?? "—"}</div>
        </div>
      </div>

      <div className="relative flex flex-wrap gap-1.5 mb-4">
        {(fin.technology_stack || []).slice(0, 5).map((t) => (
          <span key={t} className="text-[11px] px-2 py-0.5 rounded-md bg-teal-400/20 border border-teal-400/35 text-teal-100 backdrop-blur-sm">{t}</span>
        ))}
      </div>

      <div className="relative grid grid-cols-3 gap-2 mb-4">
        <Mini label="TAM" value={formatUSD(com.tam_usd)} subValue={formatINR(com.tam_usd)} />
        <Mini label="SAM" value={formatUSD(com.sam_usd)} subValue={formatINR(com.sam_usd)} />
        <Mini label="SOM" value={formatUSD(com.som_usd)} subValue={formatINR(com.som_usd)} />
      </div>

      {fr.overall_recommendation && (
        <div className="relative rounded-md border border-white/[0.08] bg-black/35 backdrop-blur-sm p-3 mb-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-teal-300/90 mb-1">Founder verdict</div>
          <div className="text-sm italic leading-relaxed line-clamp-3 text-white/90">"{fr.overall_recommendation}"</div>
        </div>
      )}

      <div className="relative mt-auto flex gap-2">
        <Button onClick={onOpen} data-testid="featured-open" className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold">
          Open Workspace <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
        <Link to={`/projects/${FEATURED}/pitch`}>
          <Button variant="outline" data-testid="featured-pitch" className="bg-black/30 backdrop-blur-sm">Pitch Deck</Button>
        </Link>
      </div>
    </div>
  );
}
function Mini({ label, value, subValue }) {
  return (
    <div className="rounded-md border border-white/[0.08] bg-black/30 backdrop-blur-sm p-2">
      <div className="text-[9px] uppercase tracking-[0.14em] text-white/50">{label}</div>
      <div className="text-base mono font-semibold mt-0.5 text-white">{value}</div>
      {subValue && <div className="text-[10px] mono text-white/55 leading-tight">{subValue}</div>}
    </div>
  );
}

// ===========================================================================
// New Project — premium form with quick-start chips + validation
// ===========================================================================
const QUICK_STARTS = [
  { id: "sepsis",   label: "Sepsis · India" },
  { id: "amr",      label: "AMR Surveillance" },
  { id: "maternal", label: "Maternal Health" },
  { id: "tb",       label: "TB Screening" },
];

export function NewProject() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ problem: "", population: "", constraints: "", geography: "" });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({
    problem:     form.problem.trim().length     >= 10 ? null : "At least 10 characters.",
    population:  form.population.trim().length  >= 10 ? null : "At least 10 characters.",
    constraints: form.constraints.trim().length >= 10 ? null : "At least 10 characters.",
    geography:   form.geography.trim().length   >= 10 ? null : "At least 10 characters.",
  }), [form]);
  const valid = !errors.problem && !errors.population && !errors.constraints && !errors.geography;

  const applyChip = (id) => {
    const s = DEMO_SCENARIOS.find((x) => x.id === id);
    if (s) { setForm({ ...s.inputs }); setTouched({}); }
  };

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const { run_id } = await apiPost("/innovate", form);
      toast.success("Venture started — streaming live");
      navigate(`/projects/${run_id}/workflow`);
    } catch (e) {
      toast.error(e.message);
      setSubmitting(false);
    }
  };

  useInsights(
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">How it runs</div>
        <ol className="space-y-2 text-xs text-white/70 leading-relaxed">
          <li className="flex gap-2"><span className="shrink-0 w-4 h-4 rounded-full border border-teal-400/40 text-teal-300 text-[9px] flex items-center justify-center font-semibold">1</span><span>You frame the problem.</span></li>
          <li className="flex gap-2"><span className="shrink-0 w-4 h-4 rounded-full border border-teal-400/40 text-teal-300 text-[9px] flex items-center justify-center font-semibold">2</span><span>12 specialised AI agents run sequentially (≈15–17 min).</span></li>
          <li className="flex gap-2"><span className="shrink-0 w-4 h-4 rounded-full border border-teal-400/40 text-teal-300 text-[9px] flex items-center justify-center font-semibold">3</span><span>Watch nodes light up live in the AI Workflow view.</span></li>
          <li className="flex gap-2"><span className="shrink-0 w-4 h-4 rounded-full border border-teal-400/40 text-teal-300 text-[9px] flex items-center justify-center font-semibold">4</span><span>Auto-routes to your Overview when complete.</span></li>
        </ol>
      </div>
      <div className="rounded-md border border-white/[0.06] bg-white/[0.015] p-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-teal-300/80 mb-1">Pro tip</div>
        <div className="text-xs text-white/70 leading-relaxed">Be specific. "Early sepsis in rural India at PHCs with intermittent power" beats "diagnose sepsis".</div>
      </div>
    </div>, []);

  return (
    <div className="px-8 py-8 max-w-3xl mx-auto" data-testid="new-project">
      <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Innovation Pipeline</div>
      <h1 className="text-2xl font-semibold tracking-tight mt-1">Generate a healthcare venture</h1>
      <p className="text-sm text-white/60 mt-1">Frame the problem — Aignoz Innovation OS runs the full 12-agent pipeline live and routes you to a venture-ready brief.</p>

      {/* Quick-start chips */}
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-2">Quick start</div>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_STARTS.map((q) => (
            <button key={q.id} type="button" onClick={() => applyChip(q.id)} data-testid={`quickstart-${q.id}`}
              className="text-[11px] px-3 py-1.5 rounded-full border border-white/[0.08] text-white/70 hover:border-teal-400/40 hover:text-teal-100 hover:bg-teal-400/[0.06] transition">
              {q.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 glass rounded-xl border-white/[0.06] p-6 space-y-4">
        <FormField label="Problem" required err={touched.problem && errors.problem}>
          <Textarea data-testid="input-problem" rows={3}
            className={`bg-white/[0.02] ${touched.problem && errors.problem ? "border-rose-400/50 focus-visible:ring-rose-400/40" : ""}`}
            value={form.problem}
            onChange={(e) => setForm({ ...form, problem: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, problem: true }))}
            placeholder="e.g. Early sepsis diagnosis in rural India" />
        </FormField>
        <FormField label="Target Population" required err={touched.population && errors.population}>
          <Textarea data-testid="input-population" rows={2}
            className={`bg-white/[0.02] ${touched.population && errors.population ? "border-rose-400/50 focus-visible:ring-rose-400/40" : ""}`}
            value={form.population}
            onChange={(e) => setForm({ ...form, population: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, population: true }))}
            placeholder="e.g. Patients in primary health centres with suspected infection" />
        </FormField>
        <FormField label="Constraints" required err={touched.constraints && errors.constraints}>
          <Textarea data-testid="input-constraints" rows={2}
            className={`bg-white/[0.02] ${touched.constraints && errors.constraints ? "border-rose-400/50 focus-visible:ring-rose-400/40" : ""}`}
            value={form.constraints}
            onChange={(e) => setForm({ ...form, constraints: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, constraints: true }))}
            placeholder="e.g. Low-cost, point-of-care, minimal lab infrastructure" />
        </FormField>
        <FormField label="Geography" required err={touched.geography && errors.geography}>
          <Input data-testid="input-geography"
            className={`bg-white/[0.02] ${touched.geography && errors.geography ? "border-rose-400/50 focus-visible:ring-rose-400/40" : ""}`}
            value={form.geography}
            onChange={(e) => setForm({ ...form, geography: e.target.value })}
            onBlur={() => setTouched((t) => ({ ...t, geography: true }))}
            placeholder="e.g. India (Tier 3 cities and rural)" />
        </FormField>

        <Button data-testid="submit-run-button"
          onClick={() => { setTouched({ problem: true, population: true, constraints: true, geography: true }); submit(); }}
          disabled={!valid || submitting}
          className="w-full bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold disabled:opacity-40 disabled:cursor-not-allowed">
          {submitting
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting…</>
            : <><Zap className="w-4 h-4 mr-2" />Generate Venture</>}
        </Button>
        {!valid && (
          <p className="text-[11px] text-white/40 text-center">All four fields are required (min 10 characters each).</p>
        )}
      </div>
    </div>
  );
}

function FormField({ label, required, err, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">{label}{required && <span className="text-teal-300 ml-1">*</span>}</span>
        {err && <span className="text-[10px] text-rose-300/90">{err}</span>}
      </div>
      {children}
    </label>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

// ===========================================================================
// Projects list
// ===========================================================================
export function ProjectsList() {
  const navigate = useNavigate();
  const { runs, loading } = useRunsList();
  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto" data-testid="projects-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Workspace</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1">Innovation Pipeline</h1>
        </div>
        <Button onClick={() => navigate("/projects/new")} className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold"><Rocket className="w-4 h-4 mr-2" />New venture</Button>
      </div>
      <div className="glass rounded-xl overflow-hidden border-white/[0.06]">
        <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-[0.18em] text-white/40 border-b border-white/[0.04]">
          <div className="col-span-3">Company</div>
          <div className="col-span-5">Problem</div>
          <div className="col-span-1 text-right">Status</div>
          <div className="col-span-1 text-right">Inn.</div>
          <div className="col-span-1 text-right">Inv.</div>
          <div className="col-span-1 text-right">Age</div>
        </div>
        {loading && [0,1,2].map((i) => <Skeleton key={i} className="h-10 m-3" />)}
        {!loading && runs.map((r) => (
          <button key={r.run_id} onClick={() => navigate(`/projects/${r.run_id}/overview`)}
            data-testid={`project-row-${r.run_id}`}
            className="w-full grid grid-cols-12 items-center px-4 py-2.5 text-sm border-b border-white/[0.04] hover:bg-white/[0.025] text-left">
            <div className="col-span-3 truncate font-medium">{r.company_name || <span className="text-white/30 italic">—</span>}</div>
            <div className="col-span-5 truncate text-white/60">{r.problem}</div>
            <div className="col-span-1 text-right"><StatusDot status={r.status} /></div>
            <div className="col-span-1 text-right mono text-teal-200">{r.scores?.innovation_score?.score ?? "—"}</div>
            <div className="col-span-1 text-right mono text-sky-200">{r.scores?.investor_attractiveness_score?.score ?? "—"}</div>
            <div className="col-span-1 text-right text-[10px] text-white/40">{relTime(r.created_at)}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
function StatusDot({ status }) {
  const map = { complete: "bg-emerald-400", running: "bg-teal-300 pulse-dot", failed: "bg-rose-400", cancelled: "bg-white/30" };
  return <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/60"><span className={`w-1.5 h-1.5 rounded-full ${map[status] || "bg-white/30"}`} />{status}</span>;
}

// ===========================================================================
// Demo Mode
// ===========================================================================
export function DemoMode() {
  const navigate = useNavigate();
  const startDemo = async (s) => {
    try {
      const { run_id } = await apiPost("/innovate", s.inputs);
      toast.success(`Generating: ${s.title}`);
      navigate(`/projects/${run_id}/overview`);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto" data-testid="demo-page">
      <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Workspace</div>
      <h1 className="text-2xl font-semibold tracking-tight mt-1">Demo scenarios</h1>
      <p className="text-sm text-white/60 mt-1">Pre-built healthcare problems. One click runs the full 12-agent pipeline.</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {DEMO_SCENARIOS.map((s) => {
          const img = SCENARIO_IMAGES[s.id];
          const isSepsis = s.id === "sepsis";
          return (
            <div key={s.id} data-testid={`demo-card-${s.id}`}
              className="relative overflow-hidden rounded-xl border border-white/[0.06] hover:border-teal-400/30 transition group">
              <div className="relative aspect-[16/10]">
                {img && (
                  <img src={img} alt="" loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                )}
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,11,14,0.05) 0%, rgba(10,11,14,0.55) 55%, rgba(10,11,14,0.95) 100%)" }} />
                <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
                  <div className="text-[9px] uppercase tracking-[0.18em] text-white/80 bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded">Scenario</div>
                  {isSepsis && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-400/20 text-emerald-200 border border-emerald-400/40 backdrop-blur-sm">Pre-run</span>}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="text-base font-semibold text-white">{s.title}</div>
                  <div className="text-[11px] text-white/75 mt-1 line-clamp-2">{s.blurb}</div>
                </div>
              </div>
              <div className="p-3 flex gap-2 bg-[#0A0B0E]/80 backdrop-blur-sm">
                {isSepsis && (
                  <Button size="sm" onClick={() => navigate(`/projects/${FEATURED}/overview`)} className="bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold flex-1" data-testid={`view-demo-${s.id}`}>
                    View Demo <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                )}
                <Button size="sm" variant={isSepsis ? "outline" : "default"}
                  className={!isSepsis ? "bg-teal-400 hover:bg-teal-300 text-slate-950 font-semibold flex-1" : "flex-1"}
                  onClick={() => startDemo(s)} data-testid={`run-live-${s.id}`}><Rocket className="w-3 h-3 mr-1.5" />Run Live</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===========================================================================
// Problem Library
// ===========================================================================
export function ProblemLibrary() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(Object.keys(PROBLEM_LIBRARY)[0]);
  const items = PROBLEM_LIBRARY[filter] || [];
  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto" data-testid="library-page">
      <div className="text-[10px] uppercase tracking-[0.18em] text-teal-300/80">Workspace</div>
      <h1 className="text-2xl font-semibold tracking-tight mt-1">Problem Library</h1>
      <p className="text-sm text-white/60 mt-1">Pre-framed healthcare problems, ready to launch.</p>
      <div className="flex gap-1.5 flex-wrap mt-5">
        {Object.keys(PROBLEM_LIBRARY).map((cat) => (
          <button key={cat} onClick={() => setFilter(cat)} data-testid={`lib-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === cat ? "bg-teal-400/15 border-teal-400/40 text-teal-100" : "border-white/[0.08] text-white/60 hover:bg-white/[0.04]"}`}>
            {cat}
          </button>
        ))}
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((p, i) => (
          <button key={i} data-testid={`lib-item-${i}`}
            onClick={async () => {
              try {
                const { run_id } = await apiPost("/innovate", p.inputs);
                toast.success("Venture started");
                navigate(`/projects/${run_id}/overview`);
              } catch (e) { toast.error(e.message); }
            }}
            className="bento glass p-4 text-left">
            <div className="text-[10px] uppercase tracking-[0.14em] text-teal-300/80 mb-1">{p.inputs.geography}</div>
            <div className="text-sm font-semibold">{p.title}</div>
            <div className="text-xs text-white/60 mt-1 line-clamp-2">{p.inputs.problem}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// Agents Catalog (developer tier)
// ===========================================================================
export function AgentsCatalog() {
  const [data, setData] = useState(null);
  useEffect(() => { apiGet("/agents").then(setData).catch(() => setData({ agents: [] })); }, []);
  if (!data) return <Skeleton className="h-64 m-8" />;
  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto" data-testid="agents-catalog">
      <div className="text-[10px] uppercase tracking-[0.18em] text-amber-400/80">Developer</div>
      <h1 className="text-2xl font-semibold tracking-tight mt-1">Agent Workflow</h1>
      <p className="text-sm text-white/60 mt-1">The 12 specialized agents that compose every Aignoz Innovation OS venture. Internal tool.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
        {data.agents.map((a) => (
          <div key={a.event_name} data-testid={`agent-${a.event_name}`} className="bento glass p-4">
            <div className="text-[10px] uppercase tracking-[0.14em] text-teal-300/80 mono">{a.event_name}</div>
            <div className="text-sm font-semibold mt-1">{a.name}</div>
            <div className="text-xs text-white/60 mt-1 line-clamp-2">{a.description}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
